import type { ContainerDetail } from '@brain/contracts'
import { Button, Card, Chip, cn, EmptyState, ListRow, toast } from '@brain/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  Archive,
  ArchiveRestore,
  CheckCircle2,
  FileText,
  Lock,
  Mic,
  MoreHorizontal,
  Pencil,
  ShieldOff,
  Sparkles,
  Star,
  Timer,
  Trash2,
  Users,
} from 'lucide-react'
import { DropdownMenu } from 'radix-ui'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CompleteProjectDialog } from '../../components/CompleteProjectDialog.tsx'
import { formatDate } from '../../components/ContainerList.tsx'
import { DecisionLog } from '../../components/decisions/DecisionLog.tsx'
import { EditContainerDialog } from '../../components/EditContainerDialog.tsx'
import { ProjectFiles } from '../../components/files/ProjectFiles.tsx'
import { CONTAINER_ICON } from '../../components/icons.ts'
import { FactList } from '../../components/lists/FactList.tsx'
import { SelectToggle, useSelection } from '../../components/lists/Selection.tsx'
import { LiveResumeBanner } from '../../components/live/LiveResumeBanner.tsx'
import { MdPreview } from '../../components/MdPreview.tsx'
import { MeetingList } from '../../components/meetings/MeetingList.tsx'
import { NewNoteMenu, NoteList, relativeDay } from '../../components/NoteList.tsx'
import { PageTree } from '../../components/note/PageTree.tsx'
import { RepoSources } from '../../components/sources/RepoSources.tsx'
import { TaskList } from '../../components/TaskList.tsx'
import { ThreeCol } from '../../components/ThreeCol.tsx'
import { TimeTrackingDialog } from '../../components/TimeTrackingDialog.tsx'
import { TaskViews } from '../../components/tasks/TaskViews.tsx'
import {
  containerQuery,
  noteQuery,
  notesQuery,
  useContainerAction,
  useFavorites,
  useToggleFavorite,
} from '../../lib/hooks.ts'
import { containerTimeQuery } from '../../lib/time-tracking.ts'
import { openLiveStart, useCurrentContainer } from '../../lib/ui-state.ts'

type Tab = 'overview' | 'notes' | 'tasks' | 'meetings' | 'decisions' | 'files' | 'sources'
const TABS: Tab[] = ['overview', 'notes', 'tasks', 'meetings', 'decisions', 'files', 'sources']

export const Route = createFileRoute('/_app/c/$containerId')({
  // `file`: project file whose preview is open (ADR 0017; search hits and citations link here)
  // `doc`: document of a Git source whose preview is open (ADR 0019)
  validateSearch: (s: Record<string, unknown>): { tab?: Tab; file?: string; doc?: string } => ({
    ...(TABS.includes(s.tab as Tab) ? { tab: s.tab as Tab } : {}),
    ...(typeof s.file === 'string' && /^[0-9a-f-]{36}$/.test(s.file) ? { file: s.file } : {}),
    ...(typeof s.doc === 'string' && /^[0-9a-f-]{36}$/.test(s.doc) ? { doc: s.doc } : {}),
  }),
  component: ContainerPage,
})

const LIST_ROUTE: Record<ContainerDetail['type'], string> = {
  project: '/projekte',
  area: '/bereiche',
  resource: '/ressourcen',
}

function ContainerPage() {
  const { t } = useTranslation()
  const { containerId } = Route.useParams()
  const { tab = 'overview', file, doc } = Route.useSearch()
  const q = useQuery(containerQuery(containerId))
  useCurrentContainer(containerId)
  const c = q.data

  return (
    <ThreeCol
      list={
        <NoteList
          containerId={containerId}
          title={c?.title ?? t('common.loading')}
          back={
            c
              ? c.status === 'archived'
                ? { to: '/archiv', label: t('para.archive') }
                : { to: LIST_ROUTE[c.type], label: t(`para.${c.type}`) }
              : undefined
          }
        />
      }
    >
      {q.isError ? (
        <div className="flex flex-1 items-center justify-center">
          <EmptyState title={t('container.notFound')} description={t('container.notFoundText')} />
        </div>
      ) : !c ? (
        <p className="p-8 text-[13px] text-meta">{t('common.loading')}</p>
      ) : (
        <ContainerContent key={c.id} c={c} tab={tab} file={file} doc={doc} />
      )}
    </ThreeCol>
  )
}

function ContainerHeader({ c }: { c: ContainerDetail }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const action = useContainerAction(c.id)
  const favorites = useFavorites()
  const toggleFav = useToggleFavorite()
  const [dialog, setDialog] = useState<'edit' | 'complete' | 'delete' | 'time' | null>(null)
  const timeTracking = useQuery(containerTimeQuery(c.id))
  const isFav = (favorites.data ?? []).some((f) => f.entityId === c.id)
  const isOwner = c.role === 'owner'
  const archived = c.status === 'archived'
  const Icon = CONTAINER_ICON[c.type]
  const run = (kind: 'archive' | 'unarchive') =>
    action.mutate(
      { kind },
      {
        onSuccess: () => toast(t(kind === 'archive' ? 'container.archivedToast' : 'container.restoredToast')),
        onError: () => toast(t('common.error'), { tone: 'attention' }),
      },
    )

  return (
    <header className="flex flex-col gap-3 px-4 pt-4 md:px-8 md:pt-6">
      <div data-tauri-drag-region className="flex items-start gap-3">
        <span className="mt-1 flex size-9 shrink-0 items-center justify-center rounded-tile bg-grey-2 text-text-2">
          {c.icon ? <span className="text-[18px]">{c.icon}</span> : <Icon size={18} strokeWidth={1.9} />}
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[24px] text-text-1 leading-tight">{c.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Chip>{t(`containerType.${c.type}`)}</Chip>
            <Chip tone={archived ? 'neutral' : c.status === 'on_hold' ? 'apricot' : 'green'}>
              {t(`containerStatus.${c.status}`)}
            </Chip>
            <Chip title={t(`visibility.${c.visibility}`)}>
              {c.visibility === 'shared' ? (
                <Users size={12} strokeWidth={2} />
              ) : (
                <Lock size={12} strokeWidth={2} />
              )}
              {t(`visibility.${c.visibility}`)}
            </Chip>
            {c.aiPolicy !== 'default' && (
              <Chip tone="sky" title={t(`aiPolicy.hint.${c.aiPolicy}`)}>
                {c.aiPolicy === 'no_ai' ? (
                  <ShieldOff size={12} strokeWidth={2} />
                ) : (
                  <Sparkles size={12} strokeWidth={2} />
                )}
                {t(`aiPolicy.${c.aiPolicy}`)}
              </Chip>
            )}
            {c.clientName && <Chip>{c.clientName}</Chip>}
            {timeTracking.data?.enabled && (
              <button type="button" onClick={() => setDialog('time')} title={t('timeTracking.chipTitle')}>
                <Chip tone="green">
                  <Timer size={12} strokeWidth={2} />
                  {timeTracking.data.project.name}
                </Chip>
              </button>
            )}
            {c.targetDate && <Chip>{t('container.until', { date: formatDate(c.targetDate) })}</Chip>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            size={32}
            variant="ghost"
            aria-pressed={isFav}
            aria-label={t(isFav ? 'favorites.remove' : 'favorites.add')}
            title={t(isFav ? 'favorites.remove' : 'favorites.add')}
            onClick={() => toggleFav.mutate({ entityType: 'container', entityId: c.id, on: !isFav })}
          >
            <Star size={16} strokeWidth={1.9} className={cn(isFav && 'fill-current text-pop')} />
          </Button>
          {!archived && (
            <Button
              size={32}
              variant="secondary"
              onClick={() => openLiveStart(c.id)}
              title={t('live.startLive')}
            >
              <Mic size={16} strokeWidth={1.9} />
              <span className="hidden sm:inline">{t('live.start')}</span>
            </Button>
          )}
          {c.type === 'project' && !archived && isOwner && (
            <Button
              size={32}
              variant="secondary"
              onClick={() => setDialog('complete')}
              className="hidden sm:inline-flex"
            >
              <CheckCircle2 size={16} strokeWidth={1.9} />
              {t('container.complete')}
            </Button>
          )}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button size={32} variant="ghost" aria-label={t('common.more')}>
                <MoreHorizontal size={16} strokeWidth={1.9} />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={6}
                className="z-50 min-w-52 rounded-card border border-line-1 bg-card p-1.5 shadow-2"
              >
                <MenuItem icon={Pencil} label={t('container.edit')} onSelect={() => setDialog('edit')} />
                <MenuItem icon={Timer} label={t('timeTracking.menu')} onSelect={() => setDialog('time')} />
                {c.type === 'project' && !archived && isOwner && (
                  <MenuItem
                    icon={CheckCircle2}
                    label={t('container.complete')}
                    onSelect={() => setDialog('complete')}
                  />
                )}
                {isOwner &&
                  (archived ? (
                    <MenuItem
                      icon={ArchiveRestore}
                      label={t('container.unarchive')}
                      onSelect={() => run('unarchive')}
                    />
                  ) : (
                    <MenuItem icon={Archive} label={t('container.archive')} onSelect={() => run('archive')} />
                  ))}
                {isOwner && (
                  <MenuItem
                    icon={Trash2}
                    label={t('container.delete')}
                    danger
                    onSelect={() => setDialog('delete')}
                  />
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
      {dialog === 'edit' && <EditContainerDialog container={c} onClose={() => setDialog(null)} />}
      {dialog === 'time' && <TimeTrackingDialog containerId={c.id} onClose={() => setDialog(null)} />}
      {dialog === 'complete' && <CompleteProjectDialog container={c} onClose={() => setDialog(null)} />}
      {dialog === 'delete' && (
        <ConfirmDelete
          title={c.title}
          onCancel={() => setDialog(null)}
          onConfirm={() =>
            action.mutate(
              { kind: 'delete' },
              {
                onSuccess: () => {
                  toast(t('container.deletedToast'))
                  void navigate({ to: LIST_ROUTE[c.type] })
                },
                onError: () => toast(t('common.error'), { tone: 'attention' }),
              },
            )
          }
        />
      )}
    </header>
  )
}

function ConfirmDelete({
  title,
  onCancel,
  onConfirm,
}: {
  title: string
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation()
  return (
    <Card className="flex flex-wrap items-center gap-3 border-apricot bg-apricot-bg px-4 py-3">
      <p className="min-w-0 flex-1 text-[13px] text-apricot-fg">{t('container.deleteConfirm', { title })}</p>
      <Button size={32} variant="ghost" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
      <Button size={32} variant="danger" onClick={onConfirm}>
        {t('container.delete')}
      </Button>
    </Card>
  )
}

function MenuItem({
  icon: Icon,
  label,
  onSelect,
  danger,
}: {
  icon: typeof Pencil
  label: string
  onSelect: () => void
  danger?: boolean
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={cn(
        'flex h-9 cursor-pointer items-center gap-2.5 rounded-tile px-3 text-[13px] outline-none data-[highlighted]:bg-grey-2',
        danger ? 'text-apricot-fg' : 'text-text-1',
      )}
    >
      <Icon size={15} strokeWidth={1.9} />
      {label}
    </DropdownMenu.Item>
  )
}

function ContainerContent({
  c,
  tab,
  file,
  doc,
}: {
  c: ContainerDetail
  tab: Tab
  file?: string | undefined
  doc?: string | undefined
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col pb-12">
      <ContainerHeader c={c} />
      <nav
        className="mt-4 flex gap-1 overflow-x-auto border-line-1 border-b px-4 md:px-8"
        aria-label={t('container.tabs')}
      >
        {TABS.map((x) => (
          <Link
            key={x}
            to="/c/$containerId"
            params={{ containerId: c.id }}
            search={x === 'overview' ? {} : { tab: x }}
            className={cn(
              '-mb-px border-b-2 px-3 py-2.5 font-medium text-[13px] transition-colors duration-150',
              tab === x ? 'border-pop text-text-1' : 'border-transparent text-meta hover:text-text-1',
            )}
          >
            {t(`container.tab.${x}`)}
          </Link>
        ))}
      </nav>
      <div className="px-4 pt-5 md:px-8">
        {tab === 'overview' && <Overview c={c} />}
        {tab === 'notes' && <NotesTab c={c} />}
        {tab === 'tasks' && <TaskViews containerId={c.id} />}
        {tab === 'meetings' && (
          <div className="flex flex-col gap-3">
            <LiveResumeBanner containerId={c.id} />
            <MeetingList containerId={c.id} />
          </div>
        )}
        {tab === 'decisions' && <DecisionLog containerId={c.id} />}
        {tab === 'files' && <ProjectFiles c={c} selected={file} />}
        {tab === 'sources' && <RepoSources c={c} selected={doc} />}
      </div>
    </div>
  )
}

function Overview({ c }: { c: ContainerDetail }) {
  const { t } = useTranslation()
  const brief = useQuery({ ...noteQuery(c.briefNoteId ?? ''), enabled: Boolean(c.briefNoteId) })
  const notes = useQuery(notesQuery({ containerId: c.id, limit: 6 }))
  const excerpt = (brief.data?.contentMd ?? '')
    .split('\n')
    .filter((l) => l.trim())
    .slice(0, 8)
    .join('\n')
  return (
    <div className="@container">
      <div className="grid gap-4 @4xl:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-4">
          {c.description && <p className="max-w-2xl text-[14px] text-text-2">{c.description}</p>}
          {c.briefNoteId && (
            <Card className="p-5">
              <div className="flex items-center justify-between">
                <h2 className="meta-label">{t('container.brief')}</h2>
                <Link
                  to="/n/$noteId"
                  params={{ noteId: c.briefNoteId }}
                  className="font-medium text-[13px] text-pop-fg"
                >
                  {t('container.openBrief')}
                </Link>
              </div>
              {excerpt ? (
                <MdPreview md={excerpt} className="mt-3" />
              ) : (
                <p className="mt-3 text-[13px] text-text-2">{t('container.briefEmpty')}</p>
              )}
            </Card>
          )}
          <Card className="p-3">
            <h2 className="px-2 pt-1 pb-2 meta-label">{t('container.openTasks')}</h2>
            <TaskList containerId={c.id} />
          </Card>
        </div>
        <div className="flex flex-col gap-4">
          <Card className="h-fit p-3">
            <div className="flex items-center justify-between px-2 pb-2">
              <h2 className="meta-label">{t('container.recentNotes')}</h2>
              <NewNoteMenu containerId={c.id} compact />
            </div>
            {(notes.data?.items ?? []).length === 0 && (
              <p className="px-2 py-3 text-[13px] text-meta">{t('note.emptyList')}</p>
            )}
            {(notes.data?.items ?? []).map((n) => (
              <Link key={n.id} to="/n/$noteId" params={{ noteId: n.id }} className="block">
                <ListRow
                  leading={<FileText size={16} strokeWidth={1.9} />}
                  title={n.title || t('note.untitled')}
                  trailing={
                    <time dateTime={n.createdAt} className="text-[11px] text-meta">
                      {relativeDay(n.createdAt)}
                    </time>
                  }
                />
              </Link>
            ))}
          </Card>
          <FactList containerId={c.id} />
        </div>
      </div>
    </div>
  )
}

function NotesTab({ c }: { c: ContainerDetail }) {
  const sel = useSelection()
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end gap-2">
        <SelectToggle sel={sel} />
        <NewNoteMenu containerId={c.id} />
      </div>
      <Card className="p-2">
        <PageTree containerId={c.id} sel={sel} />
      </Card>
    </div>
  )
}
